/**
 * A function to create the mutations to set the sellers ids as a global app setting in
 * the store and to load it from the local storage.
 * 
 * @param {Object} [storage] generated with the `createNamespacedLocalStorageFunctions`
 *  function present in the `local-storage` file in the store directory. Is an optional
 *  parameter to store the value of this mutation in the local storage with the namespace
 *  of the correspondent module of the store.
 * @param {String} [prefix] optional prefix to set to the variables names. Very useful to
 *  have different sellers ids. For example: 'clientsSellersIds' and 'competitorsSellersIds'.
 * 
 * @returns {Object} with the mutation to set the value in the store and another mutation
 *  to load the value from the local storage.
 */
export default (storage, prefix = '') => {
  const storageKey = `appSettings${prefix.charAt(0).toUpperCase() + prefix.slice(1)}SellersIds`
  const stateKey = prefix ? `${prefix.charAt(0).toLowerCase() + prefix.slice(1)}SellersIds` : 'sellersIds';

  /**
   * AVOID USING THIS MUTATION DIRECTLY. Please use the action with the same name.
   *
   * Set the sellers ids in the app to use only these sellers and not all of them.
   *
   * @param {Object} state
   * @param {Number[]} ids
   */
  function set(state, ids) {
    if (ids.some(id => typeof id !== 'number' || Number.isNaN(id))) {
      throw new Error('All the sellers ids must be numbers')
    }

    /* Get the unique values of the given ids */
    const seen = new Set()
    const uniqueIds = []
    ids.forEach(id => {
      if (!seen.has(id)) {
        seen.add(id)
        uniqueIds.push(id)
      }
    })

    state.appSettings[stateKey] = uniqueIds
    storage.setItem(storageKey, uniqueIds)
  }


  /**
   * Load an parse the sellers ids present in the local storage and set them
   * in the app setting.
   *
   * @param {Object} state
   */
  function load(state) {
    const sellersIds = storage.getItem(storageKey)

    if (sellersIds) {
      set(state, sellersIds.split(',').map(id => Number(id)))
    }
  }


  // Create the mutations names based on the prefix
  let setName;
  let loadName;

  if (!prefix) {
    setName = 'setAppSettingsSellersIds'
    loadName = 'loadAppSettingsSellersIdsFromStorage'
  } else {
    setName = `setAppSettings${prefix.charAt(0).toUpperCase() + prefix.slice(1)}SellersIds`
    loadName = `loadAppSettings${prefix.charAt(0).toUpperCase() + prefix.slice(1)}SellersIdsFromStorage`
  }

  return {
    [loadName]: load,
    [setName]: set
  }
}